/**
 * ZPL Template Generators for Zebra Thermal Printers
 *
 * IMPORTANT: Printers feed labels in portrait orientation.
 * To achieve landscape viewing, we use rotated text (^A0R) and barcode (^BCR).
 *
 * Label Sizes (at 203 DPI: 1 inch = 203 dots):
 * - Vial Label: 50mm x 75mm landscape view, printed as ^PW400^LL600 with ^A0R text (excludes 40mm auxiliary area)
 * - Receipt Label: 4" x 4" = ^PW812^LL812
 * - Delivery Label: 3.5" x 1.125" landscape view, printed as ^PW228^LL711 with ^A0R text
 */

import { ThermalLabelData, getPharmacyInfo, getBalanceLabelText, getRxTotalLabelText } from '../components/printing/ThermalLabelPrint';
import { ThermalReceiptData } from '../components/printing/ThermalReceiptPrint';
import { DeliveryLabelData } from '../components/printing/DeliveryLabelPrint';
import { formatDispenseQuantity } from './formatters';
import { formatPrescriberName } from './prescriberDisplay';
import { resolveFulfillmentType } from './fulfillmentType';
import {
  AUX_STICKER_COUNT,
  AUX_ZPL_FONT_STEPS,
  AUX_ZPL_METRICS,
  fitAuxWarning,
} from './auxWarningLayout';

/**
 * Font configuration for labels.
 * When custom fonts are downloaded to the printer, reference them here.
 * Without custom fonts, bold is simulated using the double-print technique.
 */
export interface FontConfig {
  /** Regular font path on printer (e.g. 'E:NOTOSR.TTF') */
  regular?: string;
  /** Bold font path on printer (e.g. 'E:NOTOSB.TTF') */
  bold?: string;
}

// Convert inches to dots at 203 DPI
const DPI = 203;
const inchesToDots = (inches: number) => Math.round(inches * DPI);

/**
 * Truncate text to fit within a given width
 */
function truncateText(text: string, maxChars: number): string {
  if (!text) return '';
  return text.length > maxChars ? text.substring(0, maxChars - 3) + '...' : text;
}

/**
 * Format date for label display
 */
function formatDate(dateString: string): string {
  if (!dateString) return '';
  const date = new Date(dateString);
  const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${days[date.getDay()]} ${date.getDate()}-${months[date.getMonth()]}-${date.getFullYear()}`;
}

/**
 * Format expiry date for display: DD/MM/YYYY or MM/YYYY if no day
 */
function formatExpiryForDisplay(expiry: string): string {
  if (!expiry) return '';
  if (/^\d{2}\/\d{4}$/.test(expiry)) return expiry;
  if (/^\d{4}-\d{2}-\d{2}$/.test(expiry)) {
    const [year, month, day] = expiry.split('-');
    return `${day}/${month}/${year}`;
  }
  if (/^\d{4}-\d{2}$/.test(expiry)) {
    const [year, month] = expiry.split('-');
    return `${month}/${year}`;
  }
  return expiry;
}

/**
 * Escape special characters for ZPL
 */
function escapeZpl(text: string): string {
  if (!text) return '';
  // Replace special characters that might interfere with ZPL
  return text
    .replace(/\^/g, ' ')
    .replace(/~/g, ' ')
    .replace(/\n/g, ' ')
    .replace(/\r/g, '');
}

/**
 * Split long text into multiple lines
 */
function wrapText(text: string, maxCharsPerLine: number, maxLines: number): string[] {
  if (!text) return [''];

  const words = text.split(' ');
  const lines: string[] = [];
  let currentLine = '';

  for (const word of words) {
    if (currentLine.length + word.length + 1 <= maxCharsPerLine) {
      currentLine += (currentLine ? ' ' : '') + word;
    } else {
      if (currentLine) lines.push(currentLine);
      currentLine = word;
      if (lines.length >= maxLines) break;
    }
  }

  if (currentLine && lines.length < maxLines) {
    lines.push(currentLine);
  }

  // Truncate last line if we hit max lines
  if (lines.length === maxLines && currentLine.length > 0) {
    const lastIdx = lines.length - 1;
    if (lines[lastIdx].length > maxCharsPerLine - 3) {
      lines[lastIdx] = lines[lastIdx].substring(0, maxCharsPerLine - 3) + '...';
    }
  }

  return lines;
}

const BASE_DPI = 203;

/**
 * Generate ZPL for auxiliary warning labels in the reserved strip
 * on the right side of the vial label.
 *
 * Layout: the aux strip is 5 stacked 10mm stickers (each line IS a physical
 * label that peels off and gets stuck on the vial). Each sticker gets one
 * warning, sized to fill its full sticker area.
 *
 * Coordinate space (rotated label, ^A0R text):
 *  - X axis: across 50mm label height (0–400 dots). Each sticker = 80 dots.
 *            X counts DOWN the strip: sticker i spans X(400 - i*80) (its top
 *            edge) down to 80 dots below that, and an ^A0R glyph row hangs
 *            BELOW its ^FO origin.
 *  - Y axis: along label length. Aux area extends from just after the main
 *            content divider (Y≈625) all the way to the right edge of the
 *            extended canvas (^LL1290). That's ≈660 dots ≈ 82mm of
 *            per-sticker writing width — double the old ~41mm.
 *            Header/main content is untouched.
 *  - Max 5 warnings (one per sticker).
 *
 * GP-1046 — the dispensary reported warnings printing "cut off" and "bleeding
 * into each other". Both came from the fixed 22-dot font: anything past its
 * ~90-char budget was truncated mid-sentence (~39 % of real FDB warnings), and
 * the block sat 4 dots off the sticker's top edge with no bottom gutter, so ink
 * landed on the perforation. Now each warning gets the largest font from
 * AUX_ZPL_FONT_STEPS at which it wraps in FULL (see fitAuxWarning), and the
 * wrapped block is centred so both gutters are equal.
 */
function generateAuxiliaryWarningsZpl(
  warnings: { description: string; sortOrder: number }[],
  X: (n: number) => number,
  Y: (n: number) => number,
  F: (n: number) => number,
  mTop: number,
  mLeft: number,
): string {
  if (!warnings || warnings.length === 0) return '';

  let zpl = '';

  const maxWarnings = Math.min(warnings.length, AUX_STICKER_COUNT);

  // Start right at the left edge of the physical sticker strip. The main
  // content divider ends at Y≈600; the physical aux stickers begin just
  // after. Y=625 leaves a 3mm gutter so the text never prints onto the
  // tear-off line. User-confirmed: "where they start is good".
  const auxStartY = Y(625);

  // Right edge: hug Y=1285, 5 dots shy of the ^LL1290 canvas edge. That's
  // ≈82mm of per-sticker width — double the old ~41mm, per user request.
  const auxEndY = Y(1285);
  const auxUsableWidth = auxEndY - auxStartY; // ≈ 660 dots ≈ 82mm

  // Label height = 400 dots, divided into 5 equal zones (one per sticker).
  const zoneSize = X(80); // 400 / 5 = 80 dots per zone (~10mm each)
  // GP-1046: an EQUAL gutter at both ends of every sticker. The old layout had
  // a 4-dot top margin and no bottom margin, which is what put ink on the
  // perforation between two stickers ("bleed into the next label").
  const zoneGutter = F(6);
  const usableZoneHeight = Math.max(F(8), zoneSize - 2 * zoneGutter);

  // Font sizes are tuned at 203dpi like every other coordinate here, so scale
  // the ladder with F() before measuring against the (already scaled) sticker.
  const fontSteps = AUX_ZPL_FONT_STEPS.map((step) => F(step));

  for (let i = 0; i < maxWarnings; i++) {
    const warningText = escapeZpl(warnings[i].description);
    const fit = fitAuxWarning(warningText, {
      usableWidth: auxUsableWidth,
      usableHeight: usableZoneHeight,
      fontSteps,
      ...AUX_ZPL_METRICS,
    });
    if (fit.lines.length === 0) continue;

    // ^A0 wants integer dot sizes; the ladder is already integral after F().
    const fontH = Math.max(1, Math.round(fit.font));
    const fontW = fontH; // square glyph box — matches the main label's ^A0R calls
    const lineSpacing = Math.max(fontH + 1, Math.round(fit.lineHeight));

    // Centre the block vertically inside its sticker so the top and bottom
    // gutters are equal. X counts down the strip and glyph rows hang below
    // their origin, so the first row's origin sits `slack/2` below the top
    // gutter and the last row still lands above the bottom gutter.
    const blockHeight = fit.lines.length * lineSpacing;
    const slack = Math.max(0, usableZoneHeight - blockHeight);
    let currentX = X(400 - i * 80) - zoneGutter - Math.floor(slack / 2);

    for (let lineIdx = 0; lineIdx < fit.lines.length; lineIdx++) {
      zpl += `^FO${currentX + mTop},${auxStartY + mLeft}^A0R,${fontH},${fontW}^FD${fit.lines[lineIdx]}^FS\n`;
      currentX -= lineSpacing;
    }
  }

  return zpl;
}

/**
 * Printer configuration for vial labels.
 * Use this to handle DPI differences and media centering on wider printers.
 */
export interface VialLabelPrinterConfig {
  /**
   * Printer DPI (most Zebra desktop printers are 203 or 300).
   * If you print at 300dpi but your math assumes 203dpi, everything looks "thin/small".
   */
  dpi?: 203 | 300 | number;

  /**
   * Total printhead width in dots.
   * Examples:
   *  - 4" @203dpi = 812
   *  - 4" @300dpi = 1200
   *  - 2" @203dpi = 406
   *  - 3" @203dpi = 609
   */
  printheadWidthDots?: number;

  /**
   * Set true if your label roll is physically centered in a wider printer.
   * This is the classic cause of "prints as a thin strip in the middle".
   */
  centerMedia?: boolean;

  /**
   * Rotate the entire label 180° (flip upside down).
   * Use this if the label prints upside down on your printer.
   */
  rotate180?: boolean;

  /** Manual X/Y home offset (in dots) */
  homeX?: number;
  homeY?: number;

  /** Content margins in dots - shifts all content inward */
  marginTop?: number;     // Shifts content down from yellow header
  marginBottom?: number;  // Shifts content up from barcode area
  marginLeft?: number;    // Shifts content right from left edge
  marginRight?: number;   // Shifts content left from right edge

  /** Custom fonts downloaded to the printer */
  fonts?: FontConfig;
}

/**
 * Generate ZPL for Vial Label
 *
 * Label: 120mm W × 50mm H (landscape orientation)
 * Layout top-to-bottom:
 *   - Yellow header: 15mm strip (full width)
 *   - White content: 25mm (main area)
 *   - Pink auxiliary: 10mm strip (right-aligned, partial width, NOT printed)
 *
 * ZPL canvas (printer feeds portrait, we rotate text with ^A0R):
 *   - ^PW400 = 50mm (label height as print width)
 *   - ^LL = label length along feed direction
 *
 * Coordinate mapping (at 203dpi):
 * - X axis: across 50mm height (0-400 dots)
 *   - X=400 = TOP of label (yellow header, ~15mm)
 *   - X=0 = BOTTOM of label (barcode area)
 * - Y axis: along 120mm width (0-960 dots, we use ~600)
 *   - Y=0 = LEFT edge
 *   - Y=600+ = RIGHT edge
 *
 * Config: dpi, printheadWidthDots, centerMedia, marginTop/marginLeft
 */
export function generateVialLabelZpl(
  data: ThermalLabelData,
  cfg: VialLabelPrinterConfig = {}
): string {
  const dpi = cfg.dpi ?? BASE_DPI;
  const s = dpi / BASE_DPI;

  // Canvas: ^PW400 ^LL600 at 203dpi (extends to 1290 when auxiliary warnings
  // are present). 1290 dots ≈ 161mm — gives the aux strip ~82mm of width
  // (double the old ~41mm). Header, pharmacy info, Rx, barcode, etc. all
  // stay at the same X/Y — only the canvas LENGTH changes so the aux strip
  // has room to breathe.
  const hasAuxWarnings = data.auxiliaryWarnings && data.auxiliaryWarnings.length > 0;
  const labelPw = Math.round(400 * s);
  const labelLl = Math.round((hasAuxWarnings ? 1290 : 600) * s);

  // If not provided, assume printhead == label width (safe default).
  // If you're on a 4" printer printing 50mm labels, set printheadWidthDots accordingly.
  const printheadPw = cfg.printheadWidthDots ?? labelPw;

  const autoHomeX =
    cfg.centerMedia && printheadPw > labelPw ? Math.round((printheadPw - labelPw) / 2) : 0;

  const homeX = cfg.homeX ?? autoHomeX;
  const homeY = cfg.homeY ?? 0;

  // Scale helpers (all your coordinates/fonts were tuned at 203dpi)
  const X = (n: number) => Math.round(n * s);
  const Y = (n: number) => Math.round(n * s);
  const F = (n: number) => Math.max(1, Math.round(n * s));

  const rxNumber = escapeZpl(data.rxNumber || '');
  // Convert to title case (e.g., "SMITH, John" -> "Smith, John")
  const toTitleCase = (str: string) => str.toLowerCase().replace(/\b\w/g, c => c.toUpperCase());
  const patientName = escapeZpl(toTitleCase(data.patientName || ''));  // No truncation - never cut off patient name
  const drugDescription = escapeZpl((data.drugDescription || '').toUpperCase());  // ALL CAPS, no truncation
  const genericName = escapeZpl((data.genericName || '').toUpperCase());  // ALL CAPS
  const strength = escapeZpl(data.strength || '');
  const din = escapeZpl(data.din || '');
  // GP-665: partial/balance fills print "X of Y" instead of the full dispense
  // qty, plus a bold (BALANCE OWING)/(BALANCE) indicator line below the qty line.
  const { qtyText: balanceQtyText, indicatorText: balanceIndicatorText } = getBalanceLabelText(data);
  // GP-869: Manitoba's College requires the Rx total on the vial label. null at
  // every other site (label unchanged); throws at an MB site with no total
  // rather than printing "$0.00" — see getRxTotalLabelText.
  //
  // Placement: the qty/prescriber row (X=233), in the gutter between them.
  // Budget on that row — qtyLine starts at Y=15 and is capped at 25 chars, which
  // at ^A0R,16,16 is ≈235 dots, and the prescriber column starts at rightY=420.
  // Starting at Y=250 leaves the widest realistic total ("Rx Total: $9999.99",
  // 18 chars ≈ 160 dots at this font) ending near Y=410, clear of the doctor
  // name. Bold so the regulated figure reads at a glance without extra space.
  const rxTotalText = getRxTotalLabelText(data);
  // PRX-18: a label in a split fill prints its own share, never the fill
  // quantity (nor a balance fill's "X of Y") — see ThermalLabelData.splitLabel.
  const quantity = data.splitLabel
    ? String(data.splitLabel.quantity)
    : balanceQtyText ?? (data.quantity || data.dispQty || '');
  const form = data.form ? data.form.split(' ')[0].split('(')[0] : '';
  const manufacturer = data.manufacturer ? data.manufacturer.split(' ')[0].split('(')[0] : '';
  const qtyLine = escapeZpl(
    truncateText(`${quantity}${form ? ' ' + form : ''}${manufacturer ? ' ' + manufacturer : ''}`, 25)
  );
  // GP-573: print the prescriber exactly as Kroll's practitioner designation
  // says — "NP Salvano, Lorraine", "RPh Chau, Kevin", "Dr. Chow, Andrew". The
  // title is resolved server-side from Doc.Designation (see backend
  // common/kroll/prescriber-title.ts) and arrives already prefixed, so this
  // must NOT re-assert "Dr." the way it used to. Kroll stores prescriber names
  // properly cased (3 of 31,034 rows are all-caps on Mississauga), so no title
  // casing is applied here either — it would lowercase the title itself.
  // Release 2138 review: formatPrescriberName is a transitional shim for the
  // OLD backend that still sends an untitled name — see prescriberDisplay.ts.
  // TODO(remove after 2.3.517 backend is live)
  // Wrap to 2 lines if too long instead of truncating.
  const doctorNameFormatted = formatPrescriberName(data.doctorName, data.prescriberTitle);
  const doctorLines = doctorNameFormatted ? wrapText(escapeZpl(doctorNameFormatted), 28, 2) : [];
  const fillDate = formatDate(data.fillDate);
  const refills = data.refills || '0';

  // GP-1007: a drug packaged from multiple lots must show EVERY expiry, not just
  // one. When `lotNumbers` is present (mixed/split/sealed packs) render one
  // highlighted expiry line per distinct value; otherwise fall back to the single
  // expiry so ordinary one-lot labels stay byte-identical.
  const expiryDisplayLines = (data.lotNumbers && data.lotNumbers.length > 0
    ? data.lotNumbers.map((e) => e.expiry || '')
    : [data.expiryDate || '']
  )
    .map((e) => escapeZpl(formatExpiryForDisplay(e)))
    .filter((e) => e.length > 0);

  const directionLines = wrapText(escapeZpl(data.directions || ''), 55, 8);  // 55 chars/line, 8 lines — increased from 6 to prevent cut-off

  // PRX-18: a split label names its place in the split ("Label 2 of 3") in the
  // pack counter's slot (same row, font and start, so it bounds the barcode the same way).
  const packInfo = data.splitLabel
    ? data.splitLabel.count > 1
      ? `Label ${data.splitLabel.index} of ${data.splitLabel.count}`
      : ''
    : data.packIndex !== undefined && data.totalPacks !== undefined && data.totalPacks > 1
      ? `${data.packIndex} of ${data.totalPacks}`
      : '';

  // Generic + strength combined if both present
  const genericLine = genericName ? (strength ? `${genericName} ${strength}` : genericName) : '';

  // Directions block - use ^FB (field block) for automatic word wrapping to full width
  // When expiry is present, stop directions above X=120 to avoid overlap
  const hasLotExpiryOnLabel = expiryDisplayLines.length > 0;
  let directionsZpl = '';
  const dirStartX = X(195);
  const dirLineSpacing = X(22);
  const dirMinX = hasLotExpiryOnLabel ? X(120) : X(30); // Reserve space for lot/expiry section
  const dirMaxLines = Math.min(directionLines.length, 6);
  // GP-982: the lowest direction row actually printed — the barcode sizes itself
  // against it so the bars can never run up into the sig.
  let lowestDirectionX = Number.POSITIVE_INFINITY;
  for (let idx = 0; idx < dirMaxLines; idx++) {
    const xPos = dirStartX - idx * dirLineSpacing;
    if (xPos < dirMinX) break; // Don't overlap lot/expiry or barcode
    lowestDirectionX = xPos;
    directionsZpl += `^FO${xPos + (cfg.marginTop ?? 0)},${Y(10) + (cfg.marginLeft ?? 0)}^A0R,${F(20)},${F(20)}^FD${directionLines[idx]}^FS\n`;
  }

  // Barcode module width — ^BY2 at 203dpi, the same module the delivery label
  // uses (see generateDeliveryLabelZpl). Bar HEIGHT is computed further down,
  // once the expiry/directions rows above the barcode strip are known.
  const moduleWidth = Math.max(1, Math.round(2 * s));

  // Use ^POI to invert (rotate 180°) the entire label if needed
  const poiCommand = cfg.rotate180 ? '^POI' : '';
  // ^PR2 pins this format to 2 ips. ^PR persists on the printer, so the rate has
  // to be stated in every format; the receipt keeps ^PR4, and the vial label now
  // matches the delivery label's 2 ips for the reason documented there — its Rx
  // barcode is ladder-oriented (^BCR on portrait-fed media), so bar-edge
  // crispness, and therefore first-scan success, tracks print speed directly.

  // Apply margins
  const mTop = cfg.marginTop ?? 0;
  const mLeft = cfg.marginLeft ?? 0;

  // Right-edge Y position for right-justified items - moved further left
  const rightY = Y(420);

  // Get pharmacy info based on pharmacyId
  const pharmacy = getPharmacyInfo(data.pharmacyId);
  const pharmacyAddressLine = `${pharmacy.address}, ${pharmacy.city} ${pharmacy.province} ${pharmacy.postalCode}`;

  // Bold text helper - uses custom bold font if available, otherwise double-print simulation
  const boldR = (x: number, y: number, h: number, w: number, text: string): string => {
    if (cfg.fonts?.bold) {
      return `^FO${x},${y}^A@R,${h},${w},${cfg.fonts.bold}^FD${text}^FS`;
    }
    return `^FO${x},${y}^A0R,${h},${w}^FD${text}^FS\n^FO${x + 1},${y}^A0R,${h},${w}^FD${text}^FS`;
  };

  // GP-1007: stack the expiry line(s) in the reserved lot/expiry column. Line 0
  // sits at the original single-line position so one-lot labels are byte-identical;
  // additional lots (mixed/split/sealed packs) step down, capped so they stay
  // above the barcode.
  const MAX_EXPIRY_LINES = 4;
  const renderedExpiryLines = expiryDisplayLines.slice(0, MAX_EXPIRY_LINES);
  const expiryLineX = (i: number) => X(130) - i * F(22);
  const expiryZpl = renderedExpiryLines
    .map((exp, i) => {
      const expX = expiryLineX(i);
      return `^FO${expX + mTop},${Y(10) + mLeft}^GB${F(22)},${Y(200)},${F(22)},B^FS\n^FO${expX + mTop},${Y(15) + mLeft}^A0R,${F(18)},${F(18)}^FR^FDExp: ${exp}^FS`;
    })
    .join('\n');
  // Lowest expiry row on the label, and the right edge of that column (the ^GB
  // box runs Y(10) → Y(210)). Both feed the barcode geometry below.
  const lowestExpiryX = renderedExpiryLines.length
    ? expiryLineX(renderedExpiryLines.length - 1)
    : Number.POSITIVE_INFINITY;
  const expiryColumnEndY = Y(215);

  /**
   * Rx barcode geometry (GP-982).
   *
   * The dispensary reported vial barcodes taking two or three passes to scan
   * "even when flat", while delivery labels scan first time. Three differences,
   * all corrected here:
   *
   *  1. The field origin was NEGATIVE. Every pharmacy runs
   *     DEFAULT_VIAL_PRINTER_CONFIG (marginTop: -37, see utils/vialPrinterConfig),
   *     and the barcode was the only field low enough for `X(15) + mTop` to go
   *     under zero — the emitted command was literally `^FO-22,185`. ^FO accepts
   *     0–32000, so where those bars land is up to the firmware's parser: drop
   *     the sign and it prints 37 dots higher than the calibration asked for,
   *     clamp to 0 and it prints hard against the media edge with no gutter,
   *     reject the command and it inherits the previous field's origin. None of
   *     those is the intended position, and the last two put bar ends where a
   *     curved vial or a millimetre of media wander takes them out of the
   *     scanner's field. `barcodeX` is now derived from the margin, so the field
   *     lands on media with a real edge gutter whatever the station is calibrated
   *     to — this is the only field on the label that could go negative.
   *  2. The bars were 50 dots (≈6 mm) against the delivery label's 150 (≈18 mm) —
   *     a tiny target to aim at. The height now expands to fill whatever the
   *     layout leaves free beneath the sig/expiry block, capped at the delivery
   *     label's proven 150.
   *  3. ^PR4 vs the delivery label's ^PR2 (see the print-rate note above).
   *
   * The strip runs from `barcodeX` up to the lowest thing already printed above
   * it. Inline (Y(180)) that's the expiry column; a multi-lot pack stacks up to
   * four expiry rows down the label and squeezes the strip shut, so in that case
   * the barcode starts AFTER the expiry column instead (its ^GB ends at Y(210)),
   * where only the sig — and the pack counter it then reaches — bound it.
   */
  const barcodeGapX = F(8);
  const barcodeEdgeGutterX = F(12);
  const barcodeX = Math.max(X(15), barcodeEdgeGutterX - mTop);
  const barcodeFreeHeight = (ceilingX: number) => ceilingX - barcodeGapX - barcodeX;
  const inlineCeilingX = Math.min(lowestExpiryX, lowestDirectionX);
  // Shifted past the expiry column the symbol reaches the pack counter's column
  // (X(100), Y(420)), so that has to bound the bars too.
  const shiftedCeilingX = Math.min(lowestDirectionX, packInfo ? X(100) : Number.POSITIVE_INFINITY);
  const shiftBarcodePastExpiry =
    barcodeFreeHeight(inlineCeilingX) < Y(50) &&
    barcodeFreeHeight(shiftedCeilingX) > barcodeFreeHeight(inlineCeilingX);
  const barcodeY = shiftBarcodePastExpiry ? expiryColumnEndY + Y(25) : Y(180);
  // Floor at the pre-GP-982 fixed height: a multi-lot + multi-pack label's
  // shifted strip (bounded by the pack counter at X(100)) can leave less than
  // 50 dots free, and bars shorter than today's are a regression on the exact
  // labels this fix exists for — 50 dots is also the GS1 minimum for CODE128
  // at 203 dpi. Better a bar end near the pack counter than an unscannable code.
  const barHeight = Math.max(
    Y(50),
    Math.min(Y(150), barcodeFreeHeight(shiftBarcodePastExpiry ? shiftedCeilingX : inlineCeilingX)),
  );

  return `
^XA
^PW${printheadPw}^LL${labelLl}^LH${homeX},${homeY}^CI28^PR2
${poiCommand}

${boldR(X(397) + mTop, Y(15) + mLeft, F(26), F(26), pharmacy.name)}
^FO${X(397) + mTop},${rightY + mLeft}^A0R,${F(26)},${F(26)}^FD${pharmacy.phone}^FS
^FO${X(375) + mTop},${Y(15) + mLeft}^A0R,${F(16)},${F(16)}^FD${pharmacyAddressLine}^FS
^FO${X(359) + mTop},${Y(15) + mLeft}^A0R,${F(16)},${F(16)}^FD${pharmacy.email}^FS

^FO${X(373) + mTop},${Y(10) + mLeft}^GB0,${Y(580)},${F(2)}^FS

${boldR(X(325) + mTop, Y(15) + mLeft, F(28), F(28), 'Rx: ' + rxNumber)}
^FO${X(325) + mTop},${Y(250) + mLeft}^A0R,${F(20)},${F(20)}^FD${fillDate}^FS
^FO${X(325) + mTop},${rightY + mLeft}^A0R,${F(18)},${F(18)}^FDRefills: ${refills}^FS

${boldR(X(290) + mTop, Y(15) + mLeft, F(28), F(26), patientName)}
${boldR(X(265) + mTop, Y(15) + mLeft, F(24), F(22), drugDescription)}

${genericLine ? `^FO${X(249) + mTop},${Y(15) + mLeft}^A0R,${F(16)},${F(16)}^FD${genericLine}^FS` : ''}
${din ? `^FO${X(249) + mTop},${rightY + mLeft}^A0R,${F(20)},${F(20)}^FDDIN: ${din}^FS` : ''}

^FO${X(233) + mTop},${Y(15) + mLeft}^A0R,${F(16)},${F(16)}^FD${qtyLine}^FS
${rxTotalText ? boldR(X(233) + mTop, Y(250) + mLeft, F(16), F(16), escapeZpl(rxTotalText)) : ''}
${doctorLines[0] ? `^FO${X(233) + mTop},${rightY + mLeft}^A0R,${F(18)},${F(18)}^FD${doctorLines[0]}^FS` : ''}
${doctorLines[1] ? `^FO${X(217) + mTop},${rightY + mLeft}^A0R,${F(18)},${F(18)}^FD${doctorLines[1]}^FS` : ''}
${balanceIndicatorText ? boldR(X(215) + mTop, Y(15) + mLeft, F(16), F(16), escapeZpl(balanceIndicatorText)) : ''}

${directionsZpl}

${expiryZpl}
${packInfo ? `^FO${X(100) + mTop},${rightY + mLeft}^A0R,${F(14)},${F(14)}^FD${packInfo}^FS` : ''}

^FO${barcodeX + mTop},${barcodeY + mLeft}^BY${moduleWidth},3,${barHeight}
^BCR,${barHeight},N,N,N
^FD${rxNumber}^FS

${hasAuxWarnings ? generateAuxiliaryWarningsZpl(data.auxiliaryWarnings!, X, Y, F, mTop, mLeft) : ''}
^XZ
`.trim();
}

/**
 * Generate ZPL for Receipt Label (4" x 4")
 * Matches the layout in ThermalReceiptPrint.tsx
 */
export function generateReceiptZpl(data: ThermalReceiptData, fonts?: FontConfig): string {
  const width = inchesToDots(4); // 812 dots
  const height = inchesToDots(4); // 812 dots

  // Title case helper function
  const toTitleCase = (str: string) => str.toLowerCase().replace(/\b\w/g, c => c.toUpperCase());

  const rxNumber = escapeZpl(data.rxNumber || '');
  const patientName = escapeZpl(toTitleCase(data.patientName || ''));  // Never truncate, always title case
  const fillDate = escapeZpl(data.fillDate || '');
  const address1 = escapeZpl(truncateText(data.address1 || '', 40));
  const address2 = escapeZpl(truncateText(data.address2 || '', 40));
  const patientPhone = escapeZpl(data.patientPhone || '');

  // Delivery info
  const deliveryType = data.deliveryType ? escapeZpl(data.deliveryType) : '';
  const shipmentDate = data.shipmentDate ? escapeZpl(data.shipmentDate) : '';

  // Drug info
  // GP-889: keep Kroll's dispense-qty decimals (2.4 → "2.4", not Math.round → "2").
  const drugDisplay = escapeZpl(
    `${formatDispenseQuantity(data.dispQty)} ${data.form || ''} ${data.brandName}`.trim()
  );
  const genericName = escapeZpl(truncateText(data.genericName || '', 40));
  const dinValue = (data.din || '').replace(/^DIN:\s*/i, '');
  const daysValue = (data.daysSupply || '').replace(/^Days:\s*/i, '');
  // GP-573: already carries the Kroll-designation title ("NP …" / "RPh …" /
  // "Dr. …") from the receipt query — don't prefix "Dr." again here.
  // Release 2138 review: formatPrescriberName restores the "Dr." prefix only
  // when an OLD backend sends an untitled name — see prescriberDisplay.ts.
  // TODO(remove after 2.3.517 backend is live)
  const doctorName = escapeZpl(
    truncateText(formatPrescriberName(data.doctorName, data.prescriberTitle), 30),
  );

  // Lot/Expiry
  const lotNumber = data.lotNumber ? escapeZpl(data.lotNumber) : '';
  const expiryDateDisplay = data.expiryDate ? formatExpiryForDisplay(data.expiryDate) : '';
  const hasLotExpiry = !!(lotNumber || expiryDateDisplay);

  // Pricing
  const cost = (data.cost || 0).toFixed(2);
  const fee = (data.fee || 0).toFixed(2);
  const total = (data.total || 0).toFixed(2);
  const waived = (data.waived || 0).toFixed(2);
  const patientPays = (data.price || 0).toFixed(2);

  // Parse insurance plan names (pipe-separated like "OBC: $4829.47|GSC: $100.00")
  const insurancePlans = (data.insurancePlanNames || '').split('|').filter(Boolean);

  // Pharmacy info based on pharmacyId
  const pharmacy = getPharmacyInfo(data.pharmacyId);
  const pharmacyName = escapeZpl(pharmacy.name);
  const pharmacyAddress = escapeZpl(pharmacy.address);
  const pharmacyLine2 = escapeZpl(
    [pharmacy.city, pharmacy.province, pharmacy.postalCode].filter(Boolean).join(' ')
  );
  const pharmacyPhone = escapeZpl(pharmacy.phone);
  const pharmacyEmail = escapeZpl(pharmacy.email);

  // Generate numeric barcode (16 digits)
  const numericBarcode = rxNumber.replace(/\D/g, '').padStart(16, '0');

  // Bold text helper - uses custom bold font if available, otherwise double-print simulation
  const boldN = (x: number, y: number, h: number, w: number, text: string): string => {
    if (fonts?.bold) {
      return `^FO${x},${y}^A@N,${h},${w},${fonts.bold}^FD${text}^FS`;
    }
    return `^FO${x},${y}^A0N,${h},${w}^FD${text}^FS\n^FO${x + 1},${y}^A0N,${h},${w}^FD${text}^FS`;
  };

  return `
^XA
^PW${width}
^LL${height}
^CI28
^PR4

${boldN(20, 20, 28, 28, pharmacyName)}
^FO20,50^A0N,18,18^FD${pharmacyAddress}^FS
^FO20,72^A0N,18,18^FD${pharmacyLine2}^FS
^FO20,94^A0N,18,18^FD${pharmacyPhone}^FS
^FO20,114^A0N,16,16^FD${pharmacyEmail}^FS

^FO20,140^GB772,2,2^FS
${boldN(150, 150, 26, 26, 'OFFICIAL PRESCRIPTION RECEIPT')}
^FO20,180^GB772,2,2^FS

${boldN(20, 195, 26, 26, 'Rx: ' + rxNumber)}
${boldN(450, 195, 22, 22, patientName)}
^FO450,220^A0N,18,18^FD${fillDate}^FS

^FO20,245^A0N,18,18^FD${address1}^FS
^FO20,267^A0N,18,18^FD${address2}^FS
${patientPhone ? `^FO550,255^A0N,18,18^FD${patientPhone}^FS` : ''}
${(deliveryType || shipmentDate) ? `^FO20,285^A0N,16,16^FD${[deliveryType ? `[${deliveryType}]` : '', shipmentDate ? `Ship: ${shipmentDate}` : ''].filter(Boolean).join('  ')}^FS` : ''}

^FO20,310^GB772,1,1^FS

${boldN(20, 325, 22, 22, drugDisplay)}
^FO20,350^A0N,18,18^FD${genericName}^FS
^FO20,375^A0N,16,16^FDDIN: ${dinValue}  Mfr: ${data.manufacturer || 'HPH'}  Days: ${daysValue}  Refills: ${data.refills || '0'}^FS
^FO20,400^A0N,20,20^FD${doctorName}^FS

^FO20,425^GB772,1,1^FS

^FO20,445^A0N,18,18^FDCost:^FS
^FO150,445^A0N,18,18^FD${cost}^FS

^FO20,470^A0N,18,18^FDFee:^FS
^FO150,470^A0N,18,18^FD${fee}^FS

^FO20,495^GB180,1,1^FS
^FO20,505^A0N,20,20^FDTotal:^FS
^FO150,505^A0N,20,20^FD${total}^FS

${insurancePlans.map((plan, i) => `^FO400,${445 + (i * 20)}^A0N,16,16^FD${escapeZpl(plan)}^FS`).join('\n')}
${parseFloat(waived) > 0 ? `^FO400,${445 + (insurancePlans.length * 20)}^A0N,16,16^FDWaived: $${waived}^FS` : ''}

^FO200,540^GB400,50,50,B^FS
^FO220,550^A0N,30,30^FR^FDPatient Pays: ${patientPays}^FS

^FO30,600^A0N,16,16^FDSignature: ${data.productVerifierName ? escapeZpl(data.productVerifierName) : '________________________'}^FS

^FO150,630^BY2,2,40
^BCN,40,N,N,N
^FD${numericBarcode}^FS

^FO200,680^A0N,16,16^FD${numericBarcode}^FS
^FO280,700^A0N,14,14^FDTx: ${rxNumber}^FS

^XZ
`.trim();
}

/**
 * Generate ZPL for Delivery Label (3.5" x 1.125" - Landscape viewing)
 * GP-375: One label per ORDER (not per RX). Barcode encodes RX number.
 * Printer feeds portrait (1.125" wide), so we use rotated text (^A0R)
 *
 * Canvas: 228 dots wide (1.125") x 711 dots tall (3.5")
 * Content rotated for landscape viewing after printing
 *
 * GP-887 layout rework — drivers couldn't read the labels:
 *  - Fonts roughly doubled. ZPL font 0 (CG Triumvirate Bold Condensed) is a
 *    narrow condensed face, so the main text rows widen the glyph box
 *    (width ≈ 1.1–1.15× height) to de-condense it; the badge/date row keeps
 *    slightly narrower glyphs to fit its horizontal budget.
 *  - Barcode column shrunk (divider 385 → 450): a 7-digit CODE128 at ^BY2
 *    needs ~220 dots incl. quiet zones; the freed dots widen the text column.
 *  - Patient name block now spans ~half the label height (was ~a quarter).
 *  - New optional second address line renders the order's delivery
 *    instructions (unit notes / buzzer / courier notes).
 * Left column budget: Y 15 → 450 (435 dots). Rows (X, top-down): name 188 /
 * first+phone 160 / rule 150 / street 118 / city 92 / instructions 66 /
 * fridge+bags flags 44 / badges+date 8 (box height 24).
 *
 * Hotfix (Jul 2026), reported from the delivery floor:
 *  - Phone was right-truncated to "(416) 648...": the first-name+phone row
 *    now shrinks its font for long lines and, if still over budget,
 *    ellipsizes only the NAME — the phone always prints in full.
 *  - Barcode took several scan attempts: bar height grows 110 → 150 dots
 *    (easier to aim) and ^PR2 slows printing for crisp ladder-bar edges.
 *    Module width stays ^BY2 — a 7-digit CODE128 at ^BY3 needs ~330 dots
 *    incl. quiet zones and cannot fit the ~260-dot barcode column.
 */
export function generateDeliveryLabelZpl(data: DeliveryLabelData, fonts?: FontConfig): string {
  // Portrait canvas dimensions (how printer sees it)
  const width = inchesToDots(1.125); // 228 dots
  const height = inchesToDots(3.5); // 711 dots

  // Match HTML layout: Last name UPPERCASE on its own line, first name + phone on second
  const lastName = escapeZpl((data.patientLastName || '').toUpperCase());
  const firstName = escapeZpl(data.patientFirstName || '');
  const phone = data.patientPhone ? escapeZpl(data.patientPhone) : '';
  // The phone number must always print in full — drivers call patients from
  // the label, and a right-truncated row eats the phone first ("(416) 648...").
  // Prefer shrinking the font; if the row still overflows the 435-dot column,
  // ellipsize the first NAME only, never the phone.
  // Budget: ~30 chars fit at h=22/w=25, ~36 chars at h=18/w=21.
  const fullFirstPhone = phone ? `${firstName} - ${phone}` : firstName;
  let firstNamePhone = fullFirstPhone;
  let firstPhoneH = 22;
  if (fullFirstPhone.length > 30) {
    firstPhoneH = 18;
    if (fullFirstPhone.length > 36) {
      firstNamePhone = phone
        ? `${truncateText(firstName, Math.max(4, 36 - phone.length - 3))} - ${phone}`
        : truncateText(firstName, 36);
    }
  }
  const firstPhoneW = Math.round(firstPhoneH * 1.15);

  // Address lines
  // GP-564: Prefix the unit/suite when present, matching the HTML label and SMS.
  const rawStreet = data.address.street || '';
  const rawUnit = data.address.unit || '';
  const streetWithUnit = rawUnit && rawStreet ? `${rawUnit} - ${rawStreet}` : rawStreet;
  const street = escapeZpl(truncateText(streetWithUnit, 38));
  const cityProvPostal = escapeZpl(
    truncateText(`${data.address.city || ''}, ${data.address.province || ''} ${data.address.postalCode || ''}`.trim(), 30)
  );
  // GP-887: second address line — delivery instructions captured on the order
  const instructions = data.deliveryInstructions
    ? escapeZpl(truncateText(data.deliveryInstructions, 40))
    : '';

  // Order info
  const rxNumber = escapeZpl(data.rxNumber || '');

  // Delivery info
  // GP-1234: the badge slot now leads with the FULFILLMENT type (pickup vs
  // delivery) taken from order.deliveryMethod — the same field the Patient 360
  // Order History "Delivery" column renders. A pickup order carries no courier
  // routing, so before this the badge slot printed nothing at all and a pickup
  // bag was indistinguishable from a courier run.
  //  - PICKUP   → 'PICKUP' outright; the routing type is meaningless here and
  //               printing it alongside would invite the same mis-sort.
  //  - DELIVERY → keep the routing type ('Courier', 'InHouse', …) which already
  //               reads as a delivery and carries more information; fall back to
  //               the plain word when no routing type is set.
  //  - unknown  → stay silent (see resolveFulfillmentType — no silent default).
  const fulfillment = resolveFulfillmentType(data.deliveryMethod);
  const routingType = data.deliveryType ? escapeZpl(data.deliveryType) : '';
  const deliveryType =
    fulfillment === 'PICKUP'
      ? 'PICKUP'
      : routingType || (fulfillment === 'DELIVERY' ? 'DELIVERY' : '');
  const shipmentDate = data.shipmentDate ? escapeZpl(data.shipmentDate) : '';

  // Bold text helper - uses custom bold font if available, otherwise double-print simulation
  const boldR = (x: number, y: number, h: number, w: number, text: string): string => {
    if (fonts?.bold) {
      return `^FO${x},${y}^A@R,${h},${w},${fonts.bold}^FD${text}^FS`;
    }
    return `^FO${x},${y}^A0R,${h},${w}^FD${text}^FS\n^FO${x + 1},${y}^A0R,${h},${w}^FD${text}^FS`;
  };

  // Two-column layout matching HTML delivery label
  // Left column: text info | Right column: barcode
  const colLeftY = 15;       // Left column Y start
  const dividerY = 450;      // Vertical divider Y position
  const colRightY = 460;     // Right column Y start

  // Adaptive shrink so long names/streets never run into the barcode column.
  const lastNameH = lastName.length > 15 ? 30 : 38;
  const lastNameW = Math.round(lastNameH * 1.1);
  const streetH = street.length > 28 ? 20 : 24;
  const streetW = Math.round(streetH * 1.15);

  // Delivery type badge (black background with reversed white text) + narcotic flag + date.
  // Budget: Y=colLeftY(15) → dividerY(450) = 435 dots. Worst case:
  // "Burnaby Courier" badge 192 + gap 8 + "SIG REQ" badge 96 + gap 8 +
  // date 120 = 424 ≤ 435. Recheck this math if fonts change.
  const BADGE_FONT_H = 18;
  const BADGE_FONT_W = 16;
  const BADGE_BOX_H = 24;
  const BADGE_CHAR_DOTS = 12;    // ~width per char for proportional A0 at W=16
  const BADGE_PADDING = 12;
  const BADGE_GAP = 8;
  const DATE_FONT_H = 20;
  const DATE_FONT_W = 18;
  const badgeBoxWidth = (chars: number, charDots: number = BADGE_CHAR_DOTS) =>
    chars * charDots + BADGE_PADDING;
  // GP-1234: the pickup designation is the one thing that must read at a glance
  // on the shelf, so it gets a taller box and bigger glyphs than the routing
  // badge. The box grows downward from X=colLeftY(15) to 15+34=49 — the flags
  // row sits at X=44, and it only renders for cold-chain/multi-bag orders,
  // which are deliveries and therefore never draw this badge. Horizontally
  // 'PICKUP' costs 6*16+12 = 108 dots, well under the 192 the longest routing
  // badge ("Burnaby Courier") already consumes, so the 435-dot left-column
  // budget is unchanged.
  const PICKUP_FONT_H = 26;
  const PICKUP_FONT_W = 22;
  const PICKUP_BOX_H = 34;
  const PICKUP_CHAR_DOTS = 16;

  // GP-521/GP-664: Nursing-home group badge. Staff sort packaged orders by home
  // after Product Verification, so the home name is the tag's sorting key and
  // gets the most prominent badge slot. It shares the same 370-dot left-column
  // budget as the delivery-type and narcotic badges; NH orders carry no
  // deliveryType (they never ride a courier), so in practice it occupies the
  // slot the delivery-type badge would have used. Truncated defensively so a
  // long home name can never overrun the divider and swallow the date.
  const NH_MAX_CHARS = 22;
  const nhGroupName = data.nhGroupName
    ? escapeZpl(
        data.nhGroupName.length > NH_MAX_CHARS
          ? `${data.nhGroupName.slice(0, NH_MAX_CHARS - 1)}…`
          : data.nhGroupName,
      )
    : '';

  let deliveryInfoZpl = '';
  let dateStartY = colLeftY;
  if (nhGroupName) {
    // Same geometry as the delivery-type badge below (uat moved these to ^FO8 with a
    // centred glyph row and BADGE_BOX_H = 24) so the two read as one stacked column.
    const nhTextX = 8 + Math.round((BADGE_BOX_H - BADGE_FONT_H) / 2);
    const nhBadgeWidth = badgeBoxWidth(nhGroupName.length);
    deliveryInfoZpl += `^FO8,${dateStartY}^GB${BADGE_BOX_H},${nhBadgeWidth},${BADGE_BOX_H}^FS\n`;
    deliveryInfoZpl += `^FO${nhTextX},${dateStartY + 5}^A0R,${BADGE_FONT_H},${BADGE_FONT_W}^FR^FD${nhGroupName}^FS\n`;
    dateStartY += nhBadgeWidth + BADGE_GAP;
  }
  if (deliveryType) {
    const isPickupBadge = fulfillment === 'PICKUP';
    const boxH = isPickupBadge ? PICKUP_BOX_H : BADGE_BOX_H;
    const fontH = isPickupBadge ? PICKUP_FONT_H : BADGE_FONT_H;
    const fontW = isPickupBadge ? PICKUP_FONT_W : BADGE_FONT_W;
    const charDots = isPickupBadge ? PICKUP_CHAR_DOTS : BADGE_CHAR_DOTS;
    // Centre the glyph row inside the reversed box (X is the vertical axis here).
    const textX = 8 + Math.round((boxH - fontH) / 2);
    const badgeWidth = badgeBoxWidth(deliveryType.length, charDots);
    // GP-521: accumulate from dateStartY instead of resetting to colLeftY, so this
    // stacks AFTER the nursing-home badge when both are present rather than printing
    // over it. With no NH badge dateStartY === colLeftY, so output is unchanged.
    deliveryInfoZpl += `^FO8,${dateStartY}^GB${boxH},${badgeWidth},${boxH}^FS\n`;
    deliveryInfoZpl += `^FO${textX},${dateStartY + 5}^A0R,${fontH},${fontW}^FR^FD${deliveryType}^FS\n`;
    dateStartY += badgeWidth + BADGE_GAP;
  }
  // GP-454: Narcotic flag badge. Abbreviated to "SIG REQ" so the badge + long
  // delivery type + date all fit within the left-column budget.
  if (data.isNarcotic) {
    const sigText = 'SIG REQ';
    const sigBadgeWidth = badgeBoxWidth(sigText.length);
    deliveryInfoZpl += `^FO8,${dateStartY}^GB${BADGE_BOX_H},${sigBadgeWidth},${BADGE_BOX_H}^FS\n`;
    deliveryInfoZpl += `^FO11,${dateStartY + 5}^A0R,${BADGE_FONT_H},${BADGE_FONT_W}^FR^FD${sigText}^FS\n`;
    dateStartY += sigBadgeWidth + BADGE_GAP;
  }
  if (shipmentDate) {
    deliveryInfoZpl += `^FO10,${dateStartY}^A0R,${DATE_FONT_H},${DATE_FONT_W}^FD${shipmentDate}^FS`;
  }

  // Cold-chain / multi-bag flags share one row above the badge row.
  // (GP-75 icon and emphasis bar dropped — they crowded the bigger fonts.)
  const flagParts: string[] = [];
  if (data.isRefrigerated) flagParts.push('KEEP REFRIGERATED 2-8C');
  if (data.multiBag) flagParts.push(`2 BAGS${data.multiBagSibling ? ` + ${escapeZpl(data.multiBagSibling)}` : ''}`);
  const flagsText = flagParts.join('   ');
  const flagsH = flagsText.length > 40 ? 13 : 16;
  const flagsZpl = flagsText ? boldR(44, colLeftY, flagsH, Math.round(flagsH * 1.15), flagsText) : '';

  // Using ^A0R = Rotated 90° clockwise for landscape viewing
  // X = vertical position (higher X = closer to top in landscape view)
  // Y = horizontal position (higher Y = closer to right in landscape view)
  //
  // ^PR2 slows the printhead to 2 ips for this format. The barcode is
  // ladder-oriented on this label (module edges form along the feed axis),
  // so bar-edge crispness — and first-scan success — depends directly on
  // print speed. ^PR persists on the printer, so every format states its own
  // rate: the vial label now pins ^PR2 for the same reason (GP-982) and the
  // receipt pins ^PR4, which keeps the 2 ips from leaking into it.
  return `
^XA
^PW${width}
^LL${height}
^CI28
^PR2

${boldR(188, colLeftY, lastNameH, lastNameW, lastName)}
^FO160,${colLeftY}^A0R,${firstPhoneH},${firstPhoneW}^FD${firstNamePhone}^FS

^FO150,${colLeftY}^GB3,${dividerY - colLeftY - 20},3^FS

^FO118,${colLeftY}^A0R,${streetH},${streetW}^FD${street}^FS
^FO92,${colLeftY}^A0R,22,25^FD${cityProvPostal}^FS
${instructions ? `^FO66,${colLeftY}^A0R,18,20^FD${instructions}^FS` : ''}

${flagsZpl}

${deliveryInfoZpl}

^FO10,${dividerY}^GB208,2,2^FS

^FO50,${colRightY + 20}^BY2,3,150
^BCR,150,Y,N,N
^FD${rxNumber}^FS

^XZ
`.trim();
}

/**
 * Generate a test label to verify printer settings
 */
export function generateTestLabelZpl(labelType: 'vial' | 'receipt' | 'delivery'): string {
  const sizes = {
    vial: { w: 4.6, h: 2.1, name: 'Vial Label' },
    receipt: { w: 4, h: 4, name: 'Receipt Label' },
    delivery: { w: 3.5, h: 1.125, name: 'Delivery Label' },
  };

  const size = sizes[labelType];
  const width = inchesToDots(size.w);
  const height = inchesToDots(size.h);

  return `
^XA
^PW${width}
^LL${height}
^CI28

^FO20,20^GB${width - 40},${height - 40},2^FS

^FO${Math.round(width / 2) - 100},${Math.round(height / 2) - 30}^A0N,30,30^FDTEST PRINT^FS
^FO${Math.round(width / 2) - 80},${Math.round(height / 2) + 10}^A0N,20,20^FD${size.name}^FS
^FO${Math.round(width / 2) - 60},${Math.round(height / 2) + 40}^A0N,16,16^FD${size.w}" x ${size.h}"^FS

^XZ
`.trim();
}
